import clsx from 'clsx';
import * as React from 'react';

import {
    type BlockTableData,
    type TableColumnDefinition,
    type TableColumnType,
    type TableView,
    table,
} from '@gitbook/doc-core';
import {
    ConfirmModal,
    type ConfirmModalProps,
    createPalette,
    type ModalProps,
    type PaletteDef,
    PalettePositionWidth,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps } from '@gitbook/spine-react';
import type { DeepReadonly } from '@gitbook/util-immutable';

import { type TableDefinitionViewCommandsParams, viewSpecs } from '../../Views';
import { definitionSpecs } from '../definitions';
import { createColumnDefsPalette } from '../palette';

interface DefinitionOptionsButtonProps {
    /** Key of the parent Table block */
    nodeKey: string;

    /** Editor instance */
    editor: EditorContainer;

    /** Current view in the table */
    view: TableView;

    /** Definition of the column */
    columnDef: TableColumnDefinition;
}

/**
 * Button to edit a column definition.
 */
export function DefinitionOptionsButton(
    props: DefinitionOptionsButtonProps &
        Pick<ButtonProps, 'className' | 'leadingIcon' | 'trailingIcon'>
): React.ReactElement {
    const {
        nodeKey,
        editor,
        view: initialView,
        columnDef: initialDefinition,
        leadingIcon,
        trailingIcon,
        className,
    } = props;

    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);

    const [modal, setModal] = React.useState<
        | null
        | { type: 'delete' }
        | { type: 'change_type'; columnType: TableColumnType }
        | {
              type: 'command';
              modal: React.ReactElement<ModalProps> | React.ReactElement<ConfirmModalProps>;
          }
    >(null);

    const updateDefinition = (definition: TableColumnDefinition) => {
        editor.change((change) => {
            table.setColumnDefinition(change, nodeKey, initialDefinition.id, definition);
        });
    };

    const refresh: () => [
        PaletteDef,
        TableDefinitionViewCommandsParams<TableView, TableColumnDefinition>,
    ] = () => {
        const tableNode = editor.value.document.assertBlock(nodeKey);
        const data = tableNode.data.toFrozenObject<BlockTableData>();
        const definition = data.definition[initialDefinition.id];

        return [
            generatePalette(data.view, definition),
            generateCommandsParams(data.view, definition),
        ];
    };

    const generateCommandsParams = (
        view: DeepReadonly<TableView>,
        definition: DeepReadonly<TableColumnDefinition>
    ): TableDefinitionViewCommandsParams<TableView, TableColumnDefinition> => {
        return {
            definition,
            view,
            setView: (newView) => {
                editor.change((change) => {
                    table.setView(change, nodeKey, newView);
                });

                return refresh();
            },
            setDefinition: (newDefinition: TableColumnDefinition) => {
                updateDefinition(newDefinition);
                return refresh();
            },
            openModal: (CommandModal) => {
                setModal({
                    type: 'command',
                    modal: <CommandModal definition={definition} onClose={() => setModal(null)} />,
                });
            },
        };
    };

    const generatePalette = (
        view: DeepReadonly<TableView>,
        definition: DeepReadonly<TableColumnDefinition>
    ): PaletteDef => {
        const spec = definitionSpecs[definition.type];
        const viewSpec = viewSpecs[view.type];

        const params = generateCommandsParams(view, definition);
        return createPalette({
            position: { width: PalettePositionWidth.MEDIUM },
            groups: [
                {
                    key: 'definition.type',
                    commands: [
                        {
                            key: 'definition.type.change',
                            title: 'Data type',
                            icon: spec.icon,
                            descendant: () => {
                                return createColumnDefsPalette(
                                    (columnType) => {
                                        setModal({
                                            type: 'change_type',
                                            columnType,
                                        });
                                    },
                                    {
                                        selectedType: definition.type,
                                    }
                                );
                            },
                        },
                    ],
                },
                ...(spec.commands ? spec.commands(params) : []),
                ...(viewSpec.definitionCommands ? viewSpec.definitionCommands(params) : []),
                {
                    key: 'definition.danger',
                    commands: [
                        {
                            key: 'definition.add',
                            title: `Add ${viewSpec.definitionName}`,
                            icon: Icon.AddColumnRight,
                            descendant: () => {
                                return createColumnDefsPalette((type) => {
                                    const curColumnIndex = [...view.columns].findIndex(
                                        (value) => definition.id === value
                                    );
                                    editor.change((change) => {
                                        const newColIndex = curColumnIndex + 1;
                                        const newCol = table.createColumnFromType(type);
                                        table.insertColumn(change, nodeKey, newCol, newColIndex);
                                    });
                                });
                            },
                        },
                        {
                            key: 'definition.hide',
                            title: `Hide ${viewSpec.definitionName}`,
                            icon: Icon.EyeOff,
                            trigger: () => {
                                editor.change((change) => {
                                    table.hideColumn(change, nodeKey, definition.id);
                                });
                            },
                        },
                        {
                            key: 'definition.delete',
                            title: `Delete ${viewSpec.definitionName}`,
                            icon: Icon.Trashcan,
                            trigger: () => {
                                setModal({
                                    type: 'delete',
                                });
                            },
                        },
                    ],
                },
            ],
        });
    };

    const onPress = () => {
        paletteHandle.open(generatePalette(initialView, initialDefinition));
    };

    /** When user confirmed (or not) the deletion of the option */
    const onCompleteDelete = (confirmed: boolean) => {
        setModal(null);

        if (confirmed) {
            editor.change((change) => {
                table.removeColumn(change, nodeKey, initialDefinition.id);
            });
        }
    };

    /** When user confirmed (or not) changing the option */
    const onCompleteChangeType = (confirmed: boolean) => {
        setModal(null);

        if (confirmed && modal?.type === 'change_type') {
            updateDefinition(table.changeColumnType(initialDefinition, modal.columnType));
        }
    };

    return (
        <>
            {modal?.type === 'delete' ? (
                <ConfirmModal
                    kind="danger"
                    title="Delete column"
                    confirmButtonText="Delete"
                    onComplete={onCompleteDelete}
                >
                    Do you really want to delete this column and all of its data?
                </ConfirmModal>
            ) : null}
            {modal?.type === 'change_type' ? (
                <ConfirmModal
                    title="Change column type"
                    confirmButtonText="Change"
                    onComplete={onCompleteChangeType}
                >
                    You are about to change this column type from {initialDefinition.type} to{' '}
                    {modal.columnType}. We will try to convert any value automatically, but you may
                    lose data if we cannot convert them.
                </ConfirmModal>
            ) : null}
            {modal?.type === 'command' ? modal.modal : null}
            <div className="relative h-full min-w-6">
                <Button
                    kind="secondary"
                    data-exclude-from-copy
                    ref={buttonRef}
                    label={definitionSpecs[initialDefinition.type].title}
                    tooltip={{
                        label: 'Column options',
                    }}
                    active={paletteHandle.opened}
                    leadingIcon={leadingIcon}
                    trailingIcon={trailingIcon}
                    iconOnly={!trailingIcon}
                    onPress={onPress}
                    size="small"
                    testID="column-options-button"
                    className={clsx(
                        'absolute top-0 right-0 bottom-0 my-auto transition focus:opacity-100 group-focus-within/tablecolumn:opacity-100 group-hover/tablecolumn:opacity-100 group-hover:opacity-100 group-focus/tablecolumn:opacity-100',
                        paletteHandle.opened ? 'opacity-100' : 'opacity-0',
                        className
                    )}
                />
            </div>
        </>
    );
}
